import { Controller, useForm } from "react-hook-form";
import { Dialog } from "components/Dialog";
import { Button } from "components/Button";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import { ManagementApiUrl } from "environment/routeSettings";
import { netCall } from "utils/netCall";
import { useEffect, useState } from "react";
import { ApartmentType, TApartment } from "types/dto";
import { toast } from "react-toastify";
import { TransparentLoading } from "components/TransparentLoading";
import { useRegion_v2 } from "contexts/regions_v2";
import { MapLocationPicker_v2 } from "components/MapLocationPicker_v2";
import { TResponseCode } from "utils/netCall/types";
import { Select } from "components/Select";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";

type TProps = {
  apartmentId: string;
  isOpen?: boolean;
  onClose: () => void;
  onSubmit: (value: TEditFormTypes) => void;
  isLoading: boolean;
};

export type TEditFormTypes = {
  address: string;
  identifierId: string;
  numberOfFloor: number;
  location: { lat: number; lng: number } | null;
  jurisdictionId: string;
  apartmentType: ApartmentType;
};

const apartmentTypeOptions = SortObjectByPropName(
  Enum2Array(ApartmentType),
  "name"
);

export function EditApartmentModal(props: TProps) {
  const { isOpen = false, onClose, onSubmit, isLoading, apartmentId } = props;

  const { t } = useTranslation();
  const { allJurisdictions } = useRegion_v2();

  const [isGettingData, setIsGettingData] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    getValues,
    trigger,
  } = useForm<TEditFormTypes>({
    defaultValues: undefined,
  });

  const getApartmentDetails = async (apartmentId: string) => {
    setIsGettingData(true);
    const res = await netCall<TApartment>({
      method: "GET",
      endPoint: `${ManagementApiUrl()}/api/Apartment/GetApartment/${apartmentId}`,
    });
    if (res.responseCode === TResponseCode.OK) {
      const record = res.results;
      setValue("address", record?.address);
      setValue("identifierId", record?.identifierId);
      // setValue("latitude", record?.latitude);
      // setValue("longitude", record?.longitude);
      setValue("location", {
        lat: record?.latitude,
        lng: record?.longitude,
      });
      setValue("numberOfFloor", record?.numberOfFloor);
      setValue("jurisdictionId", record?.jurisdictionId);
      setValue("apartmentType", record?.apartmentType);
    } else {
      toast.error(t("errorInGettingData"));
    }
    setIsGettingData(false);
  };

  const getJurisdictionData = () => {
    return allJurisdictions.find(
      (item) => item.id === getValues("jurisdictionId")
    );
  };
  useEffect(() => {
    if (isOpen && apartmentId) {
      getApartmentDetails(apartmentId);
    }
  }, [isOpen, apartmentId]);

  return (
    <Dialog
      maxWidth={false}
      dialogTitle={t("editApartment")}
      open={isOpen}
      onClose={onClose}
      canClose={!isLoading}
    >
      <form className="p-4 w-[640px]" onSubmit={handleSubmit(onSubmit)}>
        {isGettingData && <TransparentLoading />}
        <div className="flex flex-col w-full gap-y-3">
          <div className={"flex"}>
            <p className={"mr-1"}>{t("province")}</p>
            <p className={"text-primary-60 font-bold mr-1"}>
              {getJurisdictionData()?.provinceName}
            </p>
            <p className={"mr-1"}>{t("city")}</p>
            <p className={"text-primary-60 font-bold mr-1"}>
              {getJurisdictionData()?.cityName}
            </p>
            <p className={"mr-1"}>{t("jurisdiction")}</p>
            <p className={"text-primary-60 font-bold mr-1"}>
              {getJurisdictionData()?.name}
            </p>
          </div>
          <div className={"flex w-full gap-x-2"}>
            <Controller
              name={"identifierId"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("identifierId")}
                    placeholder={t("identifierId")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"flex-1"}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                  />
                );
              }}
            />
            <Controller
              name={"apartmentType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    size="small"
                    onChange={(e) => {
                      field.onChange(e?.value);
                    }}
                    className="flex-1"
                    label={t("apartmentType")}
                    options={apartmentTypeOptions}
                    initialValue={apartmentTypeOptions.find(
                      (item) =>
                        (item.value as unknown as ApartmentType) === field.value
                    )}
                    isError={!!error}
                  />
                );
              }}
            />
            <Controller
              name={"numberOfFloor"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("numberOfFloor")}
                    type={"number"}
                    placeholder={t("numberOfFloor")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"flex-1"}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                  />
                );
              }}
            />
          </div>
          <div className={"flex w-full gap-x-2"}>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <div className={"w-full h-[360px] flex-grow"}>
                  <MapLocationPicker_v2
                    className={"w-full h-full"}
                    onLocationPick={(coordinate) => {
                      field.onChange(coordinate);
                    }}
                    initialMarker={{
                      lat: watch("location")?.lat || 35.7219,
                      lng: watch("location")?.lng || 51.3347,
                    }}
                    coordinateFields={"editable"}
                    showAddress
                    onAddressResolved={(addr) => {
                      // Fill only when the field is still empty; keep existing.
                      if (!getValues("address")) setValue("address", addr);
                    }}
                    onUseAddress={(addr) => setValue("address", addr)}
                  />
                </div>
              )}
            />
            {/*<Controller*/}
            {/*  name={"latitude"}*/}
            {/*  control={control}*/}
            {/*  rules={{*/}
            {/*    required: true,*/}
            {/*  }}*/}
            {/*  render={({ field, fieldState: { error } }) => {*/}
            {/*    return (*/}
            {/*      <TextField*/}
            {/*        label={t("latitude")}*/}
            {/*        placeholder={t("latitude")}*/}
            {/*        type={"number"}*/}
            {/*        size="small"*/}
            {/*        {...field}*/}
            {/*        error={Boolean(error)}*/}
            {/*        className={"w-1/2"}*/}
            {/*        InputLabelProps={{*/}
            {/*          shrink: !!field.value,*/}
            {/*        }}*/}
            {/*      />*/}
            {/*    );*/}
            {/*  }}*/}
            {/*/>*/}
            {/*<Controller*/}
            {/*  name={"longitude"}*/}
            {/*  control={control}*/}
            {/*  rules={{*/}
            {/*    required: true,*/}
            {/*  }}*/}
            {/*  render={({ field, fieldState: { error } }) => {*/}
            {/*    return (*/}
            {/*      <TextField*/}
            {/*        label={t("longitude")}*/}
            {/*        placeholder={t("longitude")}*/}
            {/*        type={"number"}*/}
            {/*        size="small"*/}
            {/*        {...field}*/}
            {/*        error={Boolean(error)}*/}
            {/*        className={"w-1/2"}*/}
            {/*        InputLabelProps={{*/}
            {/*          shrink: !!field.value,*/}
            {/*        }}*/}
            {/*      />*/}
            {/*    );*/}
            {/*  }}*/}
            {/*/>*/}
          </div>
          <Controller
            name={"address"}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  label={t("address")}
                  placeholder={t("address")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"w-full"}
                  multiline={true}
                  minRows={2}
                />
              );
            }}
          />
        </div>
        <div className="col-span-12 flex items-center justify-end mt-3">
          <Button
            className="w-20"
            loading={isLoading}
            type="submit"
            variant="contained"
            onClick={async () => {
              const log = await trigger();
              console.log(log);
            }}
          >
            {t("edit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
